import { useState } from 'react';
import { Divider, Button, notification, Flex } from 'antd';
import { QrcodeOutlined, PoweroffOutlined } from '@ant-design/icons';
import TwoFactorAuthForm from '../auth/TwoFactorAuthForm';
import { useAuthService } from '../../services/authService';
import { useAuth } from '../../context/AuthContext';
import { notificationType } from '../../common/constants/notificationType';
import { buttonStyle, spacing, radius, fontSize } from '../../theming';
import { useTranslation } from 'react-i18next';
import classes from './twoFactorAuth.module.scss';
import profileClasses from '../../pages/Profile/style.module.scss';

export default function TwoFactorAuth() {
    const [api, contextHolder] = notification.useNotification();
    const { currentUser, fetchUser, setAuth } = useAuth();
    const { t } = useTranslation(['settings', 'confirmModal', 'apiResponses']);

    const { getGeneratedQr, enableTwoFactorAuth, disableTwoFactorAuth } = useAuthService();
    const [loading, setLoading] = useState(false);
    const [qrCode, setQrCode] = useState(null);
    const [otpLink, setOtpLink] = useState(null);
    // turning 2FA off asks for a current code; the API refuses to disable without one
    const [isDisabling, setIsDisabling] = useState(false);

    // #region handlers
    function errorNotification(error) {
        api[notificationType.ERROR]({
            message: t(`apiResponses:${error.response.data.code}.message`),
            description: t(`apiResponses:${error.response.data.code}.description`),
            placement: 'top',
        });
    }

    async function getQrCode() {
        try {
            const { data } = await getGeneratedQr();

            if (data) {
                setQrCode(data.qrCode);
                setOtpLink(data.link);
            }
        } catch (error) {
            errorNotification(error);
        }
    }

    async function turnOffTwoFactorAuth(form) {
        try {
            setLoading(true);
            const response = await disableTwoFactorAuth(form.code);

            if (response) {
                setIsDisabling(false);
                fetchUser();
            }

            setLoading(false);
        } catch (error) {
            errorNotification(error);
        } finally {
            setLoading(false);
        }
    }

    async function onFormSubmit(form) {
        try {
            setLoading(true);
            // the API signs out every other session and returns a 2FA-completed one for this device
            const { data } = await enableTwoFactorAuth(form.code);
            if (data) {
                setAuth(data.accessToken, data.refreshToken);
            }

            setLoading(false);
        } catch (error) {
            errorNotification(error);
        } finally {
            setLoading(false);
        }
    }
    // #endregion

    if (!currentUser.isTwoFactorEnable) {
        return (
            <Flex vertical justify="center">
                {contextHolder}
                <Divider orientation="left" className={classes['custom-divider']}>
                    {t('settings:2FA.step-1.title')}
                </Divider>
                <div>
                    <p style={{ margin: '0' }}>{t('settings:2FA.step-1.subtitle')}</p>
                </div>
                <Divider orientation="left" className={classes['custom-divider']}>
                    {t('settings:2FA.step-2.title')}
                </Divider>
                <div>
                    <p style={{ margin: '0' }}>{t('settings:2FA.step-2.subtitle')}</p>
                    <Button
                        type="primary"
                        icon={<QrcodeOutlined />}
                        onClick={getQrCode}
                        // undefined, not false, so a read-only page (impersonation) can still disable it
                        disabled={qrCode ? true : undefined}
                        size="large"
                        className={profileClasses.submitForm}
                        style={{
                            marginTop: spacing[4],
                            width: 'fit-content',
                        }}
                    >
                        {t('settings:2FA.step-2.button')}
                    </Button>

                    <div onClick={() => window.open(otpLink, '_blank')}>
                        {qrCode && <img src={qrCode} alt="Two-Factor Authentication QR Code" />}
                    </div>
                </div>

                <Divider orientation="left" className={classes['custom-divider']}>
                    {t('settings:2FA.step-3.title')}
                </Divider>
                <div>
                    <p style={{ margin: '0', marginBottom: spacing[3] }}>{t('settings:2FA.step-3.subtitle')}</p>
                    <div style={{ maxWidth: 350 }}>
                        <TwoFactorAuthForm
                            loading={loading}
                            showCancel={false}
                            submitDisabled={qrCode ? false : true}
                            onFormSubmit={onFormSubmit}
                            t={t}
                        />
                    </div>
                </div>
            </Flex>
        );
    }

    if (currentUser.isTwoFactorEnable) {
        return (
            <div>
                {contextHolder}
                <Divider orientation="left" className={classes['custom-divider']}>
                    {t('settings:2FA.turn-off.title')}
                </Divider>
                {isDisabling ? (
                    <div style={{ maxWidth: 350 }}>
                        <TwoFactorAuthForm
                            loading={loading}
                            showCancel={true}
                            submitDisabled={false}
                            onBackClick={() => setIsDisabling(false)}
                            onFormSubmit={turnOffTwoFactorAuth}
                            t={t}
                        />
                    </div>
                ) : (
                    <Button
                        type="primary"
                        icon={<PoweroffOutlined />}
                        onClick={() => setIsDisabling(true)}
                        loading={loading}
                        danger
                        style={{
                            ...buttonStyle.medium,
                            fontWeight: 700,
                            fontSize: fontSize.xs,
                            borderRadius: radius.huge,
                        }}
                    >
                        {t('settings:2FA.turn-off.button')}
                    </Button>
                )}
            </div>
        );
    }
}
